import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Users, Workflow } from "lucide-react";
import { PageHero, Reveal, SectionHead, GoldButton } from "@/components/bits/Reveal";
import { IMAGES, SERVICES, SERVICE_DETAILS } from "@/data/content";
import ServiceEnquiryForm from "@/components/forms/ServiceEnquiryForm";
import { useSEO } from "@/lib/seo";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  useSEO(
    service ? `${service.title} | Maqline Solutions — Workforce Solutions, Bengaluru` : "Services | Maqline Solutions",
    service ? `${service.short} Maqline Solutions, Bengaluru.` : "Maqline Solutions workforce services."
  );
  if (!service) return <Navigate to="/services" replace />;
  const extra = SERVICE_DETAILS[slug] || {};
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 4);

  return (
    <main data-testid={`service-page-${slug}`}>
      <PageHero
        overline={`Service ${service.num} — Maqline Solutions`}
        titleLines={[service.title]}
        description={service.short}
        image={IMAGES.engineer}
      />

      <section className="bg-white py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal>
              <Link to="/services" data-testid="service-back" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-muted hover:text-gold transition-colors mb-8">
                <ArrowLeft size={14} /> All Services
              </Link>
            </Reveal>
            <SectionHead overline="Overview" title={`What ${service.title.toLowerCase()} means at Maqline`} />
            <Reveal delay={0.12}>
              <p className="mt-6 text-muted leading-relaxed text-base sm:text-lg">{service.detail}</p>
            </Reveal>
            {extra.idealFor && (
              <Reveal delay={0.18}>
                <div className="mt-8 border-l-2 border-gold bg-steel rounded-sm p-6 flex gap-4">
                  <Users size={20} className="text-gold shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-gold mb-1.5">Who this is for</p>
                    <p className="text-sm text-ink/75 leading-relaxed">{extra.idealFor}</p>
                  </div>
                </div>
              </Reveal>
            )}
            <Reveal delay={0.24}>
              <h3 className="font-display text-2xl text-ink mt-12 mb-5">What's covered</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {service.points.map((p) => (
                  <div key={p} className="flex items-start gap-2.5 text-sm text-ink/80 bg-steel border border-ink/8 rounded-sm px-4 py-3.5">
                    <Check size={15} className="text-gold mt-0.5 shrink-0" /> {p}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            {extra.engagement && (
              <Reveal delay={0.15}>
                <div className="bg-darknavy noise-bg rounded-sm p-8 sticky top-28">
                  <div className="flex items-center gap-2.5 mb-6">
                    <Workflow size={18} className="text-lightgold" />
                    <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-lightgold">How engagement works</p>
                  </div>
                  <div className="space-y-0">
                    {extra.engagement.map((step, i) => (
                      <div key={i} className="relative flex gap-4 pb-6 last:pb-0">
                        {i !== extra.engagement.length - 1 && (
                          <div className="absolute left-[15px] top-8 bottom-0 w-px bg-white/12" />
                        )}
                        <div className="relative z-10 w-8 h-8 shrink-0 bg-navy border border-gold/50 rounded-sm flex items-center justify-center font-mono text-xs font-bold text-lightgold">
                          {String(i + 1).padStart(2, "0")}
                        </div>
                        <p className="text-sm text-white/70 leading-relaxed pt-1.5">{step}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <Link to={service.cta.to} data-testid="service-detail-cta">
                      <GoldButton gold className="w-full">{service.cta.label} <ArrowRight size={15} /></GoldButton>
                    </Link>
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <section className="bg-steel py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="Keep Exploring" title="Related services" />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {others.map((s) => (
              <Reveal key={s.slug}>
                <Link to={`/services/${s.slug}`} data-testid={`related-${s.slug}`} className="group block bg-white border border-ink/8 hover:border-gold/50 rounded-sm p-6 h-full transition-all duration-400 hover:-translate-y-1">
                  <span className="font-mono text-xs text-gold font-bold">{s.num}</span>
                  <h3 className="font-display text-lg text-ink mt-2 mb-2 group-hover:text-navy transition-colors leading-snug">{s.title}</h3>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold text-navy group-hover:text-gold transition-colors uppercase tracking-wider">
                    View <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10 bg-darknavy noise-bg rounded-sm p-8 sm:p-12 items-start">
              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-lightgold mb-4">Ask About This Service</p>
                <h3 className="font-display text-2xl sm:text-3xl text-white leading-snug">Have a question about {service.title}?</h3>
                <p className="text-white/55 text-sm mt-4 leading-relaxed max-w-sm">
                  Send a quick enquiry and our team will respond with specifics — coverage, timelines, candidate
                  categories and how an engagement would work for your requirement.
                </p>
                <div className="mt-8 flex items-center gap-3 text-sm text-white/60">
                  <span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0" />
                  Replies go to your email — no spam, no obligation.
                </div>
              </div>
              <div className="bg-white rounded-sm p-6 sm:p-8" data-testid="service-enquiry-section">
                <ServiceEnquiryForm serviceTitle={service.title} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
